import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { Permissions, Roles } from '../auth/rbac'
import { useAccess } from '../auth/useAccess'
import OrgMultiSelect from '../components/OrgMultiSelect'
import { api } from '../lib/api'
import { useToast } from '../ui/ToastProvider'

export default function NoticeEditPage() {
    const { id } = useParams()
    const navigate = useNavigate()
    const { user } = useAuth()
    const { guard, allow } = useAccess()
    const { show } = useToast()

    const [loading, setLoading] = useState(false)
    const [title, setTitle] = useState('')
    const [content, setContent] = useState('')
    const [targets, setTargets] = useState([]) // 선택된 대상 조직 ID 배열 (number)
    const [mappedGaOptions, setMappedGaOptions] = useState([])
    const [mappedGaLoading, setMappedGaLoading] = useState(false)
    const [attachments, setAttachments] = useState([])
    const [attachFiles, setAttachFiles] = useState([]) // 수정 시 함께 추가할 파일들
    const [deleteAttachmentIds, setDeleteAttachmentIds] = useState([]) // 저장 시 삭제할 첨부 id 목록
    const editorRef = useRef(null)

    const roles = Array.isArray(user?.roles) ? user.roles : []
    const isOperator = roles.includes(Roles.OPERATOR)
    const isInsurer =
        roles.includes(Roles.INSURER_MASTER_ADMIN) ||
        roles.includes(Roles.INSURER_MASTER_DEPUTY_ADMIN) ||
        roles.includes(Roles.INSURER_LEADER) ||
        roles.includes(Roles.INSURER_DEPUTY_LEADER) ||
        roles.includes(Roles.INSURER_OTHER_ADMIN) ||
        roles.includes(Roles.INSURER_BRANCH_MANAGER) ||
        roles.includes(Roles.INSURER_DEPUTY_BRANCH_MANAGER) ||
        roles.includes(Roles.INSURER_MANAGER) ||
        roles.includes(Roles.INSURER_EDUCATION_MANAGER) ||
        roles.includes(Roles.INSURER_PLAN_MANAGER)

    useEffect(() => {
        let ignore = false
        async function loadDetail() {
            setLoading(true)
            try {
                const res = await api.get(`/notices/${id}`)
                const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
                if (ignore) return
                const nextTitle = payload?.title || ''
                const nextContent = payload?.content || ''
                const nextTargets = Array.isArray(payload?.targetOrgIds) ? payload.targetOrgIds.map(Number) : []
                setTitle(nextTitle)
                setContent(nextContent)
                setTargets(nextTargets)
                if (editorRef.current) {
                    editorRef.current.innerHTML = nextContent
                }
                if (payload?.status && payload.status !== 'DRAFT') {
                    show('작성중(DRAFT) 공지만 수정할 수 있습니다.', 'error')
                    navigate(`/notices/${id}`)
                }

                // 첨부 목록 로드
                try {
                    const a = await api.get(`/notices/${id}/attachments`)
                    const list = a?.data?.data ?? a?.data ?? []
                    if (!ignore) setAttachments(Array.isArray(list) ? list : [])
                } catch {
                    if (!ignore) setAttachments([])
                }
            } catch {
                show('공지 상세를 불러오지 못했습니다.', 'error')
                navigate('/notices')
            } finally {
                if (!ignore) setLoading(false)
            }
        }
        loadDetail()
        return () => { ignore = true }
    }, [id, navigate, show])

    // 원수사 계정: 소속 조직에 매핑된 GA 목록 로딩
    useEffect(() => {
        let ignore = false
        async function loadMappedGAs() {
            if (!isInsurer || !user?.orgId) {
                if (!ignore) setMappedGaOptions([])
                return
            }
            setMappedGaLoading(true)
            try {
                const res = await api.get(`/orgs/${user.orgId}/mappings`)
                const data = res?.data?.data ?? res?.data ?? {}
                const mapped = Array.isArray(data.gas) ? data.gas : []
                if (!ignore) {
                    setMappedGaOptions(mapped.map(g => ({ id: g.id, name: g.name })))
                }
            } catch {
                if (!ignore) setMappedGaOptions([])
            } finally {
                if (!ignore) setMappedGaLoading(false)
            }
        }
        loadMappedGAs()
        return () => { ignore = true }
    }, [isInsurer, user])

    const onSubmit = async (e) => {
        e.preventDefault()
        if (!allow.perm([Permissions.NOTICE_CREATE])) return
        try {
            const payload = { title, content, targetOrgIds: (targets || []).map(t => Number(t)) }
            await api.patch(`/notices/${id}`, payload)

            // 첨부 삭제(저장 시점에만 반영)
            if (deleteAttachmentIds.length > 0) {
                for (const attId of deleteAttachmentIds) {
                    try {
                        await api.delete(`/notices/${id}/attachments/${attId}`)
                    } catch {
                        // 개별 삭제 실패는 전체 수정 실패로 보지 않고 넘어감
                    }
                }
                setDeleteAttachmentIds([])
            }

            // 수정 화면에서 선택한 파일들을 업로드 + 공지 첨부로 연결
            if (attachFiles.length > 0) {
                for (const file of attachFiles) {
                    try {
                        const form = new FormData()
                        form.append('file', file)
                        const up = await api.post('/files', form, {
                            headers: { 'Content-Type': 'multipart/form-data' },
                        })
                        const fileId = up?.data?.data?.fileId ?? up?.data?.fileId
                        if (!fileId) continue
                        await api.post(`/notices/${id}/attachments`, {
                            fileId,
                            filename: file.name,
                            contentType: file.type,
                            size: file.size,
                            uploadedByUserId: user?.id || null,
                        })
                    } catch {
                        // 개별 파일 실패는 전체 수정 실패로 보지 않고 넘어감
                    }
                }
                setAttachFiles([])
            }

            // 첨부 목록 새로고침
            try {
                const a = await api.get(`/notices/${id}/attachments`)
                const list = a?.data?.data ?? a?.data ?? []
                setAttachments(Array.isArray(list) ? list : [])
            } catch {
            }

            show('수정이 완료되었습니다.', 'success')
            navigate(`/notices/${id}`)
        } catch {
            show('수정에 실패했습니다.', 'error')
        }
    }

    const toggleDeleteAttachment = (att) => {
        if (!att?.id) return
        const attId = Number(att.id)
        setDeleteAttachmentIds(prev => {
            const exists = (prev || []).includes(attId)
            if (exists) return (prev || []).filter(x => x !== attId)
            return [...(prev || []), attId]
        })
    }

    return (
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold">공지 수정</h1>
                <button type="button" className="rounded border px-3 py-2 text-sm" onClick={() => navigate(`/notices/${id}`)}>
                    상세로
                </button>
            </div>

            <form onSubmit={onSubmit} className="mt-6 space-y-6 max-w-3xl">
                <div>
                    <label className="block text-sm">제목</label>
                    <input className="mt-1 w-full rounded border px-3 py-2" value={title} onChange={(e) => setTitle(e.target.value)} required />
                </div>

                <div>
                    <label className="block text-sm">대상 조직</label>
                    {isInsurer && !isOperator ? (
                        <div className="mt-1 space-y-4">
                            <div>
                                <OrgMultiSelect
                                    value={targets}
                                    onChange={setTargets}
                                    placeholder="대상 조직 검색..."
                                    collapsible={true}
                                    defaultOpen={false}
                                    title="내 조직/하위 조직 선택"
                                    scope="mine"
                                />
                                <p className="text-xs text-slate-500 mt-1">
                                    내 조직(원수사) 및 하위 조직 트리에서 대상 조직을 선택할 수 있습니다.
                                </p>
                            </div>

                            <div>
                                <div className="text-sm font-medium mb-1">매핑된 GA 대상 선택</div>
                                {mappedGaLoading ? (
                                    <div className="text-xs text-slate-500">매핑된 GA 지점을 불러오는 중입니다...</div>
                                ) : mappedGaOptions.length === 0 ? (
                                    <div className="text-xs text-red-500">
                                        매핑된 GA 지점이 없습니다. 조직 관리에서 GA 매핑을 먼저 설정해 주세요.
                                    </div>
                                ) : (
                                    <div className="border rounded p-2 max-h-48 overflow-y-auto text-sm space-y-1">
                                        {mappedGaOptions.map(opt => (
                                            <label key={opt.id} className="flex items-center gap-2 text-xs">
                                                <input
                                                    type="checkbox"
                                                    className="rounded border-slate-300"
                                                    checked={targets.includes(Number(opt.id))}
                                                    onChange={(e) => {
                                                        const idNum = Number(opt.id)
                                                        setTargets(prev => {
                                                            const set = new Set((prev || []).map(v => Number(v)))
                                                            if (e.target.checked) set.add(idNum)
                                                            else set.delete(idNum)
                                                            return Array.from(set)
                                                        })
                                                    }}
                                                />
                                                <span>{opt.name}</span>
                                            </label>
                                        ))}
                                    </div>
                                )}
                                <p className="text-xs text-slate-500 mt-1">
                                    내 조직에 매핑된 GA 조직도 대상 조직으로 선택할 수 있습니다.
                                </p>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="mt-1">
                                <OrgMultiSelect
                                    value={targets}
                                    onChange={setTargets}
                                    placeholder="대상 조직 검색..."
                                    collapsible={true}
                                    defaultOpen={false}
                                    title="대상 조직 선택"
                                    scope={Array.isArray(user?.roles) && user.roles.includes('OPERATOR') ? 'all' : 'mine'}
                                />
                            </div>
                            <p className="text-xs text-slate-500 mt-1">
                                운영자는 전체 조직, 그 외에는 내 조직과 하위 조직만 선택할 수 있습니다.
                            </p>
                        </>
                    )}
                </div>

                <div>
                    <label className="block text-sm">내용</label>
                    <div className="mt-1 border rounded">
                        <div className="border-b px-2 py-1 flex gap-2 text-xs">
                            <button
                                type="button"
                                className="px-2 py-1 rounded hover:bg-slate-100"
                                onClick={() => {
                                    if (!editorRef.current) return
                                    document.execCommand('bold')
                                    editorRef.current.focus()
                                }}
                            >
                                굵게
                            </button>
                            <button
                                type="button"
                                className="px-2 py-1 rounded hover:bg-slate-100"
                                onClick={() => {
                                    if (!editorRef.current) return
                                    document.execCommand('insertUnorderedList')
                                    editorRef.current.focus()
                                }}
                            >
                                • 목록
                            </button>
                            <button
                                type="button"
                                className="px-2 py-1 rounded hover:bg-slate-100"
                                onClick={async () => {
                                    const input = document.createElement('input')
                                    input.type = 'file'
                                    input.accept = 'image/*'
                                    input.onchange = async () => {
                                        const file = input.files && input.files[0]
                                        if (!file || !editorRef.current) return
                                        try {
                                            const form = new FormData()
                                            form.append('file', file)
                                            const up = await api.post('/files', form, {
                                                headers: { 'Content-Type': 'multipart/form-data' },
                                            })
                                            const fileId = up?.data?.data?.fileId ?? up?.data?.fileId
                                            if (!fileId) return
                                            editorRef.current.focus()
                                            const sel = window.getSelection()
                                            if (sel) {
                                                const range = document.createRange()
                                                range.selectNodeContents(editorRef.current)
                                                range.collapse(false)
                                                sel.removeAllRanges()
                                                sel.addRange(range)
                                            }
                                            const base = (api.defaults && api.defaults.baseURL) ? api.defaults.baseURL.replace(/\/$/, '') : ''
                                            const url = `${base}/files/${fileId}/preview`
                                            const img = document.createElement('img')
                                            img.src = url
                                            img.alt = file.name
                                            img.style.maxWidth = '100%'
                                            const currentSel = window.getSelection()
                                            if (currentSel && currentSel.rangeCount > 0) {
                                                const range = currentSel.getRangeAt(0)
                                                range.insertNode(img)
                                                range.collapse(false)
                                            } else {
                                                editorRef.current.appendChild(img)
                                            }
                                            setContent(editorRef.current.innerHTML)
                                        } catch {
                                        }
                                    }
                                    input.click()
                                }}
                            >
                                이미지
                            </button>
                        </div>
                        <div
                            ref={editorRef}
                            className="min-h-[160px] px-3 py-2 text-sm prose max-w-none focus:outline-none"
                            contentEditable
                            suppressContentEditableWarning
                            onInput={(e) => setContent(e.currentTarget.innerHTML)}
                        />
                    </div>
                </div>

                {/* 첨부: 기존 첨부 목록 + 추가 업로드 */}
                <div className="rounded border p-4">
                    <div className="flex items-center justify-between">
                        <h2 className="font-semibold">첨부파일</h2>
                    </div>

                    <div className="mt-3">
                        <label className="block text-sm">첨부 추가</label>
                        <div className="mt-1 flex flex-col gap-2">
                            <input
                                type="file"
                                multiple
                                className="text-sm"
                                onChange={(e) => {
                                    const files = Array.from(e.target.files || [])
                                    if (files.length === 0) return
                                    setAttachFiles(prev => [...prev, ...files])
                                    e.target.value = ''
                                }}
                            />
                            {attachFiles.length > 0 && (
                                <ul className="text-xs text-slate-700 border rounded p-2 max-h-40 overflow-y-auto">
                                    {attachFiles.map((f, idx) => (
                                        <li key={`${f.name}-${idx}`} className="flex items-center justify-between gap-2 py-0.5">
                                            <span className="truncate">{f.name}</span>
                                            <button
                                                type="button"
                                                className="text-red-500"
                                                onClick={() => setAttachFiles(prev => prev.filter((_, i) => i !== idx))}
                                            >
                                                제거
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            <p className="text-xs text-slate-500">
                                “수정 저장”을 누르면 선택한 파일이 공지에 첨부됩니다.
                            </p>
                        </div>
                    </div>

                    <ul className="mt-4 divide-y">
                        {attachments.map(att => {
                            const willDelete = deleteAttachmentIds.includes(Number(att.id))
                            return (
                                <li key={att.id} className="py-2 flex items-center justify-between text-sm">
                                    <div className="flex items-center gap-3 min-w-0">
                                        <span className={`text-slate-700 truncate ${willDelete ? 'line-through text-slate-400' : ''}`}>{att.filename}</span>
                                        <span className="text-slate-400">{att.contentType || ''}</span>
                                        <span className="text-slate-400">{att.size ? `${Math.round(att.size / 1024)} KB` : ''}</span>
                                        {willDelete && (
                                            <span className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-0.5">
                                                삭제 예정
                                            </span>
                                        )}
                                        {att.expired && (
                                            <span className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-0.5">
                                                다운로드 기간만료
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {!att.expired && !willDelete && (
                                            <>
                                                <button
                                                    type="button"
                                                    className="underline text-slate-700"
                                                    onClick={async () => {
                                                        try {
                                                            const res = await api.get(`/files/${att.fileId}/preview`, { responseType: 'blob' })
                                                            const url = URL.createObjectURL(res.data)
                                                            window.open(url, '_blank')
                                                            URL.revokeObjectURL(url)
                                                        } catch { }
                                                    }}
                                                >
                                                    미리보기
                                                </button>
                                                <button
                                                    type="button"
                                                    className="underline text-slate-700"
                                                    onClick={async () => {
                                                        try {
                                                            const res = await api.get(`/files/${att.fileId}/preview`, { responseType: 'blob' })
                                                            const blobUrl = URL.createObjectURL(res.data)
                                                            const a = document.createElement('a')
                                                            a.href = blobUrl
                                                            a.download = att.filename || att.fileId || 'download'
                                                            document.body.appendChild(a)
                                                            a.click()
                                                            a.remove()
                                                            URL.revokeObjectURL(blobUrl)
                                                        } catch { }
                                                    }}
                                                >
                                                    다운로드
                                                </button>
                                            </>
                                        )}
                                        <button
                                            type="button"
                                            className={willDelete ? 'text-slate-600 underline' : 'text-red-600'}
                                            onClick={() => toggleDeleteAttachment(att)}
                                        >
                                            {willDelete ? '삭제 취소' : '삭제'}
                                        </button>
                                    </div>
                                </li>
                            )
                        })}
                        {attachments.length === 0 && (
                            <li className="py-4 text-sm text-slate-500">첨부가 없습니다.</li>
                        )}
                    </ul>
                </div>

                <div className="pt-2 flex items-center gap-2">
                    <button
                        {...guard.disableIfNoPerm([Permissions.NOTICE_CREATE])}
                        className="rounded bg-brand-navy text-white px-4 py-2 disabled:opacity-50"
                        type="submit"
                        disabled={loading}
                    >
                        수정 저장
                    </button>
                    <button
                        type="button"
                        className="rounded border px-4 py-2"
                        onClick={() => navigate(`/notices/${id}`)}
                    >
                        취소
                    </button>
                </div>
            </form>
        </section>
    )
}


